import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '../ui/utils';
import { formatVariance } from '../../lib/utils/format';
import { memo } from 'react';

interface TrendBadgeProps {
  /**
   * Percentage change value
   * Positive = upward trend, Negative = downward, Zero = neutral
   */
  value: number;
  /**
   * Number of decimal places
   * @default 1
   */
  decimals?: number;
  /**
   * Size variant
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Show icon
   * @default true
   */
  showIcon?: boolean;
  /**
   * Invert colors (for expenses where down is good)
   * @default false
   */
  invertColors?: boolean;
  className?: string;
  /**
   * Test ID for testing
   * @default 'trend-badge'
   */
  testId?: string;
}

const sizeClasses = {
  sm: 'text-xs px-1.5 py-0.5 gap-1',
  md: 'text-sm px-2 py-1 gap-1.5',
  lg: 'text-base px-2.5 py-1.5 gap-2',
};

const iconSizes = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
  lg: 'h-4 w-4',
};

/**
 * TrendBadge - Shows trend indicator with percentage change
 * 
 * Displays colored badge with arrow icon and formatted percentage.
 * Colors automatically adjust based on positive/negative values.
 * 
 * @example
 * ```tsx
 * // Revenue increased 12.5%
 * <TrendBadge value={12.5} />
 * 
 * // Expenses decreased 5.2% (down is good)
 * <TrendBadge value={-5.2} invertColors />
 * ```
 */
export const TrendBadge = memo(function TrendBadge({
  value,
  decimals = 1,
  size = 'md',
  showIcon = true,
  invertColors = false,
  className,
  testId = 'trend-badge',
}: TrendBadgeProps) {
  const isPositive = value > 0;
  const isNegative = value < 0;
  const isNeutral = value === 0;

  // Determine color based on trend and inversion
  const shouldBeGreen = invertColors ? isNegative : isPositive;
  const shouldBeRed = invertColors ? isPositive : isNegative;

  const Icon = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;

  const colorClasses = cn(
    shouldBeGreen && 'bg-success-100 text-success-700 dark:bg-success-950 dark:text-success-400',
    shouldBeRed && 'bg-danger-100 text-danger-700 dark:bg-danger-950 dark:text-danger-400',
    isNeutral && 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-400'
  );

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-medium',
        sizeClasses[size],
        colorClasses,
        className
      )}
      data-testid={testId}
      aria-label={`Tendência: ${formatVariance(value, decimals)}`}
    >
      {showIcon && <Icon className={iconSizes[size]} aria-hidden="true" />}
      <span data-testid={`${testId}-value`}>{formatVariance(value, decimals)}</span>
    </span>
  );
});
